import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { NavLink } from "react-router-dom";
import Examination_navbar from "./Examination_navbar";
import WingsDropDown from "../Shared/WingsDropDown";
import SessionsDropDown from "../Shared/SessionsDropDown";
import ClassesDropDown from "../Shared/ClassesDropDown";
import SectionDropDown from "../Shared/SectionDropDown";
import ExamsDropDown from "../Shared/ExamsDropDown";
import SubjectsByExamSectionDropDown from "../Shared/SubjectsByExamSectionDropDown";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DataEntry = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [SessionId, setSessionId] = useState(
    localStorage.getItem("DefaultSession") || "0"
  );
  const [WingId, setWingId] = useState("0");
  const [ClassId, setClassId] = useState("0");
  const [SectionID, setSectionID] = useState("0");
  const [ExamID, setExamID] = useState("");
  const [SubjectID, setSubjectID] = useState("0");

  const [section, setSection] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listOfLedger, setLedger] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [subjectMeta, setSubjectMeta] = useState({
    name: "",
    totalMarks: 0,
    passMarks: 0,
  });

  const firstMarkInputRef = useRef(null);
  const sectionIdNum = parseInt(SectionID, 10) || 0;
  const examIdNum = parseInt(ExamID, 10) || 0;
  const subjectIdNum = parseInt(SubjectID, 10) || 0;

  const examSessionId = useMemo(() => {
    const fromSection =
      section?.SelectedClass?.Session_ID ||
      section?.SelectedClass?.Selected_Session?.Session_ID;
    if (fromSection) return parseInt(fromSection, 10);
    return parseInt(SessionId, 10) || 0;
  }, [section, SessionId]);

  const bumpList = () => setRefreshCounter((c) => c + 1);

  const canSearch =
    sectionIdNum > 0 && examIdNum > 0 && subjectIdNum > 0;

  const parseMarksMeta = (response, records) => {
    const total =
      response.SubjectTotalMarks ??
      response.subjectTotalMarks ??
      records[0]?.Total_Marks ??
      records[0]?.total_Marks ??
      0;
    const pass =
      response.SubjectPassMarks ??
      response.subjectPassMarks ??
      records[0]?.Pass_Marks ??
      records[0]?.pass_Marks ??
      0;
    const name =
      response.SubjectName ??
      response.subjectName ??
      "";
    return {
      name,
      totalMarks: Number(total) || 0,
      passMarks: Number(pass) || 0,
    };
  };

  const fetchStudents = useCallback(() => {
    if (!hasSearched || !canSearch) {
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Exam/GetStudentsBySectionSubject",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey: AccessKey,
        UserID: UserID,
        MemberType: MemberType,
        ExamID: examIdNum,
        SectionSubjectID: subjectIdNum,
        Section_Id: sectionIdNum,
      },
    })
      .then(function (response) {
        const code = response.data?.status_code;
        if (code === 1 || code === "1") {
          const records = (response.data.ListofRecords || []).map((row) => {
            const meta = parseMarksMeta(response.data, [row]);
            return {
              ...row,
              ExamData_ID: row.ExamData_ID || row.Class_ID || 0,
              Class_ID: row.ExamData_ID || row.Class_ID || 0,
              Total_Marks: meta.totalMarks,
              Pass_Marks: meta.passMarks,
              Obtained_Marks:
                row.Obtained_Marks === "" || row.Obtained_Marks == null
                  ? ""
                  : row.Obtained_Marks,
            };
          });
          const subjectInfo = parseMarksMeta(response.data, records);
          setLedger(records);
          setTotalRecords(response.data.totalRecords || records.length);
          setSubjectMeta(subjectInfo);
          setTimeout(() => {
            if (firstMarkInputRef.current) {
              firstMarkInputRef.current.focus();
            }
          }, 100);
        } else {
          setLedger([]);
          setTotalRecords(0);
          toast.info(
            response.data.status_message || "No students found.",
            { position: toast.POSITION.TOP_RIGHT }
          );
        }
        setLoading(false);
      })
      .catch(function () {
        setLedger([]);
        setTotalRecords(0);
        setLoading(false);
        toast.error("Unable to load students. Please try again.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }, [
    AccessKey,
    UserID,
    MemberType,
    hasSearched,
    canSearch,
    examIdNum,
    subjectIdNum,
    sectionIdNum,
    refreshCounter,
  ]);

  useEffect(() => {
    document.title = "Exam Data Entry";
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    if (sectionIdNum <= 0) {
      setSection({});
      return;
    }
    axios({
      method: "post",
      url: config.base_url + "Sections/GetSectionByID",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey: AccessKey,
        UserID: UserID,
        MemberType: MemberType,
        ID: sectionIdNum,
      },
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          setSection(response.data.Section || {});
        } else {
          setSection({});
        }
      })
      .catch(function () {
        setSection({});
      });
  }, [sectionIdNum, AccessKey, UserID, MemberType]);

  const runSearch = () => {
    if (!canSearch) {
      toast.warning("Please select section, exam, and subject.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    setHasSearched(true);
    bumpList();
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    setSubjectID(value);
    setHasSearched(false);
    if (
      parseInt(value, 10) > 0 &&
      sectionIdNum > 0 &&
      examIdNum > 0
    ) {
      setHasSearched(true);
      bumpList();
    }
  };

  const handleMarksChange = (studentId, newMarks) => {
    setLedger((prev) =>
      prev.map((item) =>
        item.Student_ID === studentId
          ? { ...item, Obtained_Marks: newMarks }
          : item
      )
    );
  };

  const fillSampleMarks = () => {
    if (!hasSearched || listOfLedger.length === 0) {
      toast.warning("Load students before filling sample marks.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    const total = Number(subjectMeta.totalMarks) || 100;
    const pass = Number(subjectMeta.passMarks) || Math.floor(total * 0.4);

    setLedger((prev) =>
      prev.map((item, index) => {
        let sample;
        if (index % 6 === 0) {
          sample = Math.max(0, pass - 2);
        } else if (index % 4 === 0) {
          sample = total;
        } else {
          const min = Math.max(0, pass);
          const max = total;
          sample = min + Math.floor(Math.random() * (max - min + 1));
        }
        sample = Math.min(total, Math.max(0, sample));
        return { ...item, Obtained_Marks: sample };
      })
    );

    toast.info("Sample marks filled for testing. Review before saving.", {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const validateMarksBeforeSave = () => {
    const total = Number(subjectMeta.totalMarks);
    for (const item of listOfLedger) {
      const obtained = Number(item.Obtained_Marks);
      if (item.Obtained_Marks !== "" && item.Obtained_Marks != null) {
        if (!Number.isFinite(obtained) || obtained < 0) {
          toast.error(`Invalid marks for ${item.Student_Name}.`, {
            position: toast.POSITION.TOP_RIGHT,
          });
          return false;
        }
        if (Number.isFinite(total) && obtained > total) {
          toast.error(
            `${item.Student_Name}: marks cannot exceed ${total}.`,
            { position: toast.POSITION.TOP_RIGHT }
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSaveMarks = useCallback(() => {
    if (!hasSearched || listOfLedger.length === 0) {
      toast.warning("Load students before saving marks.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    if (!validateMarksBeforeSave()) {
      return;
    }

    setSaving(true);
    const payload = listOfLedger.map((item) => ({
      Class_ID: item.ExamData_ID || item.Class_ID || 0,
      Student_ID: item.Student_ID,
      Student_Name: item.Student_Name,
      Registration_No: item.Registration_No,
      Father_Name: item.Father_Name,
      Obtained_Marks:
        item.Obtained_Marks === "" || item.Obtained_Marks == null
          ? 0
          : item.Obtained_Marks,
    }));

    axios({
      method: "post",
      url: config.base_url + "Exam/UpdateExamMarks",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey: AccessKey,
        UserID: UserID,
        MemberType: MemberType,
        ExamID: examIdNum,
        SectionSubjectID: subjectIdNum,
        Section_Id: sectionIdNum,
        ListofStudentRecords: payload,
      },
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          toast.success(response.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          bumpList();
        } else {
          toast.error(
            response.data.status_message || "Unable to save marks.",
            { position: toast.POSITION.TOP_RIGHT }
          );
        }
        setSaving(false);
      })
      .catch(function () {
        setSaving(false);
        toast.error("Network error. Please try again.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }, [
    AccessKey,
    UserID,
    MemberType,
    hasSearched,
    listOfLedger,
    examIdNum,
    subjectIdNum,
    sectionIdNum,
    subjectMeta.totalMarks,
  ]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (!saving) {
          handleSaveMarks();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleSaveMarks, saving]);

  const renderLedger = () => {
    if (listOfLedger.length > 0) {
      return listOfLedger.map((item, index) => (
        <tr key={item.Student_ID || index}>
          <td>{index + 1}</td>
          <td>{item.Registration_No}</td>
          <td>{item.Student_Name}</td>
          <td>{item.Father_Name}</td>
          <td>
            <input
              type="number"
              min="0"
              max={subjectMeta.totalMarks || undefined}
              step="0.01"
              value={item.Obtained_Marks}
              onChange={(e) =>
                handleMarksChange(item.Student_ID, e.target.value)
              }
              ref={index === 0 ? firstMarkInputRef : null}
              className="form-control marks text-center"
              style={{ width: "110px", margin: "0 auto" }}
              disabled={saving}
            />
          </td>
          <td className="text-center fw-semibold">{subjectMeta.totalMarks}</td>
          <td className="text-center fw-semibold">{subjectMeta.passMarks}</td>
        </tr>
      ));
    }

    if (hasSearched && !loading) {
      return (
        <tr>
          <td colSpan="7">
            <h4
              className="mt-3"
              style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
            >
              No students found for this section and subject.
            </h4>
          </td>
        </tr>
      );
    }

    if (!hasSearched && !loading) {
      return (
        <tr>
          <td colSpan="7">
            <h4
              className="mt-3"
              style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
            >
              Select section, exam, and subject, then click Search (or pick a
              subject to load automatically).
            </h4>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <>
      <Examination_navbar />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/ExaminationSystem/ExaminationDashboard">
                        Examinations
                      </NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Data Entry
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Section Wise Exam Data Entry</div>
              </div>
            </div>
          </div>
        </div>

        <div className="collapse show" id="collapseFilteres">
          <div className="page-content">
            <div className="container">
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Select Session:</label>
                        <select
                          className="form-select"
                          value={SessionId}
                          onChange={(e) => {
                            setSessionId(e.target.value);
                            setWingId("0");
                            setClassId("0");
                            setSectionID("0");
                            setExamID("");
                            setSubjectID("0");
                            setHasSearched(false);
                          }}
                          name="SessionID"
                        >
                          <option value="0">---All---</option>
                          <SessionsDropDown selectedSession={SessionId} />
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Select Wing:</label>
                        <select
                          name="Wing_Id"
                          value={WingId}
                          onChange={(e) => {
                            setWingId(e.target.value);
                            setClassId("0");
                            setSectionID("0");
                            setExamID("");
                            setSubjectID("0");
                            setHasSearched(false);
                          }}
                          className="form-select"
                        >
                          <option value="0">---All---</option>
                          <WingsDropDown />
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Select Class:</label>
                        <select
                          className="form-select"
                          value={ClassId}
                          onChange={(e) => {
                            setClassId(e.target.value);
                            setSectionID("0");
                            setExamID("");
                            setSubjectID("0");
                            setHasSearched(false);
                          }}
                          name="Class_ID"
                        >
                          <option value="0">Please Select</option>
                          {SessionId !== "0" && WingId !== "0" && (
                            <ClassesDropDown
                              SessionID={SessionId}
                              WingID={WingId}
                            />
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Select Section:</label>
                        <select
                          className="form-select"
                          value={SectionID}
                          onChange={(e) => {
                            setSectionID(e.target.value);
                            setExamID("");
                            setSubjectID("0");
                            setHasSearched(false);
                          }}
                          name="Section_Id"
                        >
                          <option value="0">Please Select</option>
                          {ClassId !== "0" && ClassId !== "" && (
                            <SectionDropDown ClassID={ClassId} />
                          )}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-end">
                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Select Exam:</label>
                        <select
                          className="form-select"
                          value={ExamID}
                          onChange={(e) => {
                            setExamID(e.target.value);
                            setSubjectID("0");
                            setHasSearched(false);
                          }}
                          required
                        >
                          <option value="">Please Select</option>
                          <ExamsDropDown SessionID={examSessionId} />
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <label>Select Subject:</label>
                        <select
                          className="form-select"
                          value={SubjectID}
                          onChange={handleSubjectChange}
                          name="Subject_Id"
                        >
                          <option value="0">Please Select</option>
                          {sectionIdNum > 0 && examIdNum > 0 && (
                            <SubjectsByExamSectionDropDown
                              Section_Id={sectionIdNum}
                              ExamID={examIdNum}
                            />
                          )}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group mb-3">
                        <button
                          type="button"
                          onClick={runSearch}
                          className="button button-primary w-100"
                          disabled={loading || !canSearch}
                        >
                          {loading ? "Loading..." : "Search"}
                        </button>
                      </div>
                    </div>
                    {hasSearched && listOfLedger.length > 0 && (
                      <div className="col-md-3">
                        <div className="alert alert-info py-2 mb-3">
                          {subjectMeta.name && (
                            <strong>{subjectMeta.name}</strong>
                          )}
                          <div className="small mt-1">
                            <span className="me-3">
                              <strong>Total marks:</strong> {subjectMeta.totalMarks}
                            </span>
                            <span>
                              <strong>Pass marks:</strong> {subjectMeta.passMarks}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                  <span className="text-muted">
                    {hasSearched
                      ? `${totalRecords} student(s) — enter marks and Save (Ctrl+S)`
                      : ""}
                    {hasSearched && (
                      <span className="ms-md-3 d-block d-md-inline mt-1 mt-md-0">
                        <strong>Total marks:</strong> {subjectMeta.totalMarks}
                        {" · "}
                        <strong>Pass marks:</strong> {subjectMeta.passMarks}
                      </span>
                    )}
                  </span>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={fillSampleMarks}
                      className="button button-white"
                      disabled={saving || !hasSearched || listOfLedger.length === 0}
                      title="Fill mark inputs with sample values for testing (does not save)"
                    >
                      Fill Sample Marks
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveMarks}
                      className="button button-primary"
                      disabled={saving || !hasSearched || listOfLedger.length === 0}
                    >
                      {saving ? "Saving..." : "Save Marks"}
                    </button>
                  </div>
                </div>
                <div
                  className="table-responsive"
                  style={{ maxHeight: "65vh", overflowY: "auto" }}
                >
                  <table className="table table-theme table-hover">
                    <thead
                      className="table-light"
                      style={{ position: "sticky", top: 0, zIndex: 1 }}
                    >
                      <tr>
                        <th colSpan="7">
                          {hasSearched
                            ? `Total ${totalRecords} record(s)${
                                subjectMeta.name ? ` — ${subjectMeta.name}` : ""
                              }`
                            : "Student marks"}
                        </th>
                      </tr>
                      <tr>
                        <th>Sr.</th>
                        <th>Reg. No</th>
                        <th>Student Name</th>
                        <th>Father Name</th>
                        <th className="text-center">Obtained</th>
                        <th className="text-center">Total Marks</th>
                        <th className="text-center">Pass Marks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td className="text-center" colSpan="7">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderLedger()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataEntry;
